import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getSocietyUrl } from '../../utils/urlHelpers';
import { shouldOpenSocietyAsFullPage } from '../../utils/societySeo';
import { societyService } from '../../services/societyService';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import LazyImage from '../common/LazyImage';
import SocietyPreviewModal from './SocietyPreviewModal';

const SocietyCard = ({ society, onFollowChange }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { showSuccess, showError } = useToast();
  const [isFollowing, setIsFollowing] = useState(society.is_following || false);
  const [followLoading, setFollowLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Add safety check for society object
  if (!society) {
    return null;
  }

  const openStrongPage = shouldOpenSocietyAsFullPage(society);

  const handleOpenSociety = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (openStrongPage) {
      navigate(getSocietyUrl(society));
      return;
    }
    setPreviewOpen(true);
  };

  // Handle follow/unfollow
  const handleFollowToggle = async (e) => {
    e.preventDefault(); // Prevent navigation when clicking follow button
    e.stopPropagation();

    if (!isAuthenticated) {
      showError('Please login to follow societies');
      return;
    }

    try {
      setFollowLoading(true);
      const response = await societyService.toggleFollowSociety(society.id, isFollowing);
      setIsFollowing(response.is_following);
      showSuccess(response.message);
      
      // Notify parent component if callback provided
      if (onFollowChange) {
        onFollowChange(society.id, response.is_following, response.follower_count);
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
      showError(err.message || 'Failed to update follow status');
    } finally {
      setFollowLoading(false);
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating || 0);
    const hasHalfStar = (rating || 0) % 1 !== 0;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(
        <svg key={i} className="h-3 w-3 shrink-0 fill-current text-yellow-400 sm:h-4 sm:w-4" viewBox="0 0 20 20">
          <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
        </svg>
      );
    }
    
    const halfId = `half-star-${society.id}`;
    if (hasHalfStar) {
      stars.push(
        <svg key="half" className="h-3 w-3 shrink-0 fill-current text-yellow-400 sm:h-4 sm:w-4" viewBox="0 0 20 20">
          <defs>
            <linearGradient id={halfId}>
              <stop offset="50%" stopColor="currentColor" />
              <stop offset="50%" stopColor="transparent" />
            </linearGradient>
          </defs>
          <path fill={`url(#${halfId})`} d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
        </svg>
      );
    }
    
    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(
        <svg key={`empty-${i}`} className="h-3 w-3 shrink-0 fill-current text-gray-300 sm:h-4 sm:w-4" viewBox="0 0 20 20">
          <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
        </svg>
      );
    }
    
    return stars;
  };

  const getMainImage = () => {
    if (society && society.images && Array.isArray(society.images) && society.images.length > 0) {
      return society.images[0].original_image;
    }
    return null;
  };

  const truncateDescription = (text, maxLength = 120) => {
    if (text && text.length > maxLength) {
      return text.substring(0, maxLength) + '...';
    }
    return text || 'No description available';
  };

  return (
    <div className="flex h-full min-w-0 flex-col overflow-hidden rounded-lg bg-white shadow-md transition-shadow duration-300 hover:shadow-lg">
      {/* Image — shorter on 2-col mobile */}
      <div className="relative h-28 bg-gray-200 sm:h-44 md:h-48">
        {getMainImage() ? (
          <LazyImage
            src={getMainImage()}
            alt={society.name}
            className="h-full w-full"
            objectFit="cover"
            showProgress={true}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-blue-100 to-blue-200">
            <div className="text-center px-1">
              <svg className="mx-auto mb-1 h-8 w-8 text-blue-400 sm:mb-2 sm:h-12 sm:w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <p className="text-[10px] font-medium leading-tight text-blue-600 sm:text-sm">Society Image</p>
            </div>
          </div>
        )}
        
        {/* Image count badge */}
        {(society.image_count || 0) > 0 && (
          <div className="absolute right-1 top-1 rounded-full bg-black bg-opacity-50 px-1.5 py-0.5 text-[10px] text-white sm:right-2 sm:top-2 sm:px-2 sm:py-1 sm:text-xs">
            {society.image_count || 0} photo{(society.image_count || 0) !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-grow flex-col p-2 sm:p-4 lg:p-6">
        {/* Society Name */}
        <h3 className="mb-1 line-clamp-2 text-xs font-semibold leading-snug text-gray-900 sm:mb-2 sm:text-lg md:text-xl">
          {society.name || 'Unnamed Society'}
        </h3>

        {/* Location */}
        {society.city && (
          <div className="mb-1.5 flex min-w-0 items-center text-gray-600 sm:mb-3">
            <svg className="mr-0.5 h-3 w-3 shrink-0 sm:mr-1 sm:h-4 sm:w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="truncate text-[11px] sm:text-sm">{society.city}</span>
          </div>
        )}

        {/* Rating */}
        <div className="mb-1.5 flex min-w-0 flex-wrap items-center gap-x-0.5 gap-y-0.5 sm:mb-3">
          <div className="flex shrink-0 items-center gap-px sm:gap-0.5">{renderStars(society.rating || 0)}</div>
          <span className="min-w-0 truncate text-[10px] text-gray-600 sm:ml-2 sm:text-sm">
            {society.rating_display || 'No ratings yet'}
          </span>
          {(society.total_ratings || 0) > 0 && (
            <span className="hidden text-[10px] text-gray-500 sm:ml-1 sm:inline sm:text-sm">
              ({society.total_ratings || 0})
            </span>
          )}
        </div>

        {/* Description — hide stub copy on weak (non-indexable) cards */}
        {openStrongPage ? (
          <p className="mb-2 line-clamp-2 flex-grow text-[11px] leading-snug text-gray-600 sm:mb-4 sm:line-clamp-3 sm:text-sm">
            {truncateDescription(society.description)}
          </p>
        ) : (
          <p className="mb-2 line-clamp-2 flex-grow text-[11px] leading-snug text-gray-500 sm:mb-4 sm:line-clamp-3 sm:text-sm">
            Preview available — limited profile details
          </p>
        )}

        {/* Divider and Action Buttons (primary link + icon follow) */}
        <div className="mt-auto border-t border-gray-200 pt-2 sm:pt-4">
          <div className="flex gap-1 sm:gap-3">
            {openStrongPage ? (
              <Link
                to={getSocietyUrl(society)}
                className="flex min-h-[40px] min-w-0 flex-1 items-center justify-center rounded-md bg-blue-600 px-1.5 py-1.5 text-center text-[10px] font-medium leading-tight text-white transition-colors duration-200 hover:bg-blue-700 sm:min-h-[44px] sm:px-4 sm:text-sm"
              >
                View Details
              </Link>
            ) : (
              <button
                type="button"
                onClick={handleOpenSociety}
                className="flex min-h-[40px] min-w-0 flex-1 items-center justify-center rounded-md bg-blue-600 px-1.5 py-1.5 text-center text-[10px] font-medium leading-tight text-white transition-colors duration-200 hover:bg-blue-700 sm:min-h-[44px] sm:px-4 sm:text-sm"
              >
                Preview
              </button>
            )}
            <button
              type="button"
              onClick={handleFollowToggle}
              disabled={followLoading}
              className={`flex min-h-[40px] min-w-[36px] shrink-0 items-center justify-center rounded-md px-1.5 py-1.5 text-sm font-medium transition-colors duration-200 sm:min-h-[44px] sm:min-w-[44px] sm:px-4 ${
                isFollowing
                  ? 'border-2 border-green-500 bg-white text-green-600 hover:bg-green-50'
                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
              } ${followLoading ? 'cursor-not-allowed opacity-50' : ''}`}
              title={isFollowing ? 'Unfollow society' : 'Follow society'}
              aria-label={isFollowing ? 'Unfollow society' : 'Follow society'}
              aria-pressed={isFollowing}
            >
              {followLoading ? (
                <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              ) : (
                <svg
                  className={`h-4 w-4 ${isFollowing ? 'text-green-600' : 'text-gray-700'}`}
                  fill={isFollowing ? 'currentColor' : 'none'}
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
      <SocietyPreviewModal
        open={previewOpen}
        society={society}
        onClose={() => setPreviewOpen(false)}
        onContinue={(s) => {
          setPreviewOpen(false);
          navigate(getSocietyUrl(s));
        }}
      />
    </div>
  );
};

export default SocietyCard;
